export type propsMaskType =
  | 'cpf'
  | 'cnpj'
  | 'document'
  | 'phone'
  | 'cep'
  | 'card'
  | 'cardExpiry'
  | 'money'
  | 'date'
  | 'percent'
  | 'integer'
  | 'email';

const onlyDigits = (value: string): string => value.replace(/\D/g, '');

export function maskCPF(value: string): string {
  const digits = onlyDigits(value).slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  }
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

export function maskCNPJ(value: string): string {
  const digits = onlyDigits(value).slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  }
  if (digits.length <= 12) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  }
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

export function maskDocument(value: string): string {
  const digits = onlyDigits(value);
  return digits.length <= 11 ? maskCPF(digits) : maskCNPJ(digits);
}

export function maskPhone(value: string): string {
  const digits = onlyDigits(value).slice(0, 11);
  if (digits.length === 0) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  }
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

export function maskCEP(value: string): string {
  const digits = onlyDigits(value).slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

export function maskCard(value: string): string {
  const digits = onlyDigits(value).slice(0, 19);
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
}

export function maskCardExpiry(value: string): string {
  const digits = onlyDigits(value).slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

export function maskDate(value: string): string {
  const digits = onlyDigits(value).slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

export function maskMoney(value: string): string {
  const digits = onlyDigits(value).replace(/^0+/, '');
  if (!digits) return '';
  const padded = digits.padStart(3, '0');
  const cents = padded.slice(-2);
  const intPart = padded.slice(0, -2);
  const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${withThousands},${cents}`;
}

export function maskPercent(value: string): string {
  const cleaned = value.replace(/[^\d,]/g, '');
  const firstComma = cleaned.indexOf(',');
  if (firstComma === -1) return cleaned.slice(0, 6);
  const head = cleaned.slice(0, firstComma).slice(0, 4);
  const tail = cleaned.slice(firstComma + 1).replace(/,/g, '').slice(0, 2);
  return `${head},${tail}`;
}

export function maskInteger(value: string): string {
  return onlyDigits(value);
}

export function maskEmail(value: string): string {
  return value.replace(/\s+/g, '').toLowerCase();
}

export function applyMask(type: propsMaskType, value: string): string {
  if (value == null) return '';
  switch (type) {
    case 'cpf':
      return maskCPF(value);
    case 'cnpj':
      return maskCNPJ(value);
    case 'document':
      return maskDocument(value);
    case 'phone':
      return maskPhone(value);
    case 'cep':
      return maskCEP(value);
    case 'card':
      return maskCard(value);
    case 'cardExpiry':
      return maskCardExpiry(value);
    case 'money':
      return maskMoney(value);
    case 'date':
      return maskDate(value);
    case 'percent':
      return maskPercent(value);
    case 'integer':
      return maskInteger(value);
    case 'email':
      return maskEmail(value);
    default:
      return value;
  }
}

export const unmaskDigits = (value?: string | null): string =>
  value ? value.replace(/\D/g, '') : '';

export function unmaskMoneyToCents(value?: string | null): number {
  if (!value) return 0;
  const digits = value.replace(/\D/g, '');
  if (!digits) return 0;
  const n = Number(digits);
  return Number.isFinite(n) ? n : 0;
}

export function unmaskPercentToRate(value?: string | null): number {
  if (!value) return 0;
  const cleaned = value.replace(/[^\d,]/g, '').replace(',', '.');
  const n = Number.parseFloat(cleaned);
  if (!Number.isFinite(n)) return 0;
  return n / 100;
}

export function unmaskDateToISO(value?: string | null): string | undefined {
  if (!value) return undefined;
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 8) return undefined;
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4, 8);
  return `${year}-${month}-${day}`;
}
